<template>
  <div class="wrapper">
    <div
      class="likeable rating-display"
      :title="`${rating.name} - ${
        { true: 'like', false: 'dislike', null: 'not set' }[
          '' + rating.value
        ]
      }`"
      @click.left.prevent="moveState(true)"
      @click.right.prevent="moveState(false)"
      @click.middle.prevent="resetState()"
    >
      <SvgSymbol
        :symbol="rated.name"
        :color="rated.color"
      />
    </div>
    <PlusMinus
      v-if="showButtons"
      class="plus-minus"
      @increase="moveState(true)"
      @decrease="moveState(false)"
      @reset="resetState()"
    />
  </div>
</template>

<script setup lang="ts">
import type {
  LikeServiceIncludingValue,
  LikeValue,
} from "@/stores/ratingService";
import SvgSymbol from "@/components/Svg/SvgSymbol.vue";
import { byApiName } from "@/components/HydrusRating/Symbols";
import type { RatingStatus } from "@/components/HydrusRating/Symbols";
import { computed, defineEmits, defineProps, withDefaults } from "vue";
import PlusMinus from "@/components/HydrusRating/PlusMinus.vue";

const props = withDefaults(
  defineProps<{
    rating: LikeServiceIncludingValue,
    showButtons: boolean,
  }>(),
  {
    showButtons: true,
  },
);

const emit = defineEmits<{
  (event: "set", newState: LikeValue, oldState: LikeValue): void;
}>();

const rated = computed(
  () => {
    // Guard against missing star_shape (defensive programming)
    if (!props.rating.star_shape) {
      return byApiName.circle.null;
    }
    // eslint-disable-next-line prettier/prettier
    return byApiName[
      props.rating.star_shape
    ][
      (props.rating.value ?? "null").toString() as RatingStatus
    ] ?? byApiName.circle.null;
  }
);

const stateProgression = [false, null, true];
const neutralState = null;

function moveState(up: boolean) {
  // pull current index or neutral/default
  let newStateIndex = stateProgression.indexOf(props.rating.value ?? neutralState);
  // increment it as needed
  newStateIndex += up ? 1 : -1;
  // make sure that it stays in bounds: x ∈ [0 - stateProgression.length)
  newStateIndex = Math.max(
    0,
    Math.min(stateProgression.length - 1, newStateIndex)
  );
  // translate the state back to a concrete value
  const newState = stateProgression[newStateIndex];
  // emit the new value (and the old value)
  emit("set", newState, props.rating.value);
}

function resetState() {
  emit("set", neutralState, props.rating.value);
}
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;

  .rating-display, .plus-minus {
    flex-shrink: 0;
  }
  .rating-display {
    flex-grow: 1;
    display: flex;
    align-content: center;
    justify-content: center;
  }
  .plus-minus {
    flex-grow: 0;
    margin-left: 1em;
  }
}
</style>
